// hooks/administration/users/useUsers.ts
import { useState, useCallback } from "react";
import useToken from "../../../hooks/features/auth/useToken";
import { User } from "../../../types/features/User";
import { userService } from "../../../services//userService";

export default function useUsers() {
  const { token , izilifeToken } = useToken();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const getUsers = useCallback(async (page: number = 1, perPage: number = 20) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await userService.getUsers(token, izilifeToken , page, perPage);
      setUsers(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des users");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const getUserById = useCallback(async (id: string): Promise<User | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const user = await userService.getUserById(token , izilifeToken, id);
      setSelectedUser(user);
      return user;
    } catch (err) {
      setError("Erreur lors du chargement de l'user");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);



  const createUser = useCallback(async (data: Omit<User, "id" | "created_at" | "updated_at">): Promise<User | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const newUser = await userService.createUser(token , izilifeToken, data);
      setUsers((prev) => [newUser, ...prev]);
      return newUser;
    } catch (err) {
      setError("Erreur lors de la création de l'user");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const updateUser = useCallback(async (id: string, data: Partial<User>): Promise<User | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const updatedUser = await userService.updateUser(token , izilifeToken, id, data);
      setUsers((prev) => prev.map((e) => (e.id === id ? updatedUser : e)));
      if (selectedUser?.id === id) setSelectedUser(updatedUser);
      return updatedUser;
    } catch (err: any) {
      setError("Erreur lors de la mise à jour de l'user");
      console.error(err?.response?.data);
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedUser, token]);

  const deleteUser = useCallback(async (id: string): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      await userService.deleteUser(token , izilifeToken, id);
      setUsers((prev) => prev.filter((e) => e.id !== id));
      if (selectedUser?.id === id) setSelectedUser(null);
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de l'user");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [selectedUser, token]);

 



  return {
    users,
    selectedUser,
    loading,
    error,
    pagination,
    getUsers,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
  
  };
}